import Debug from 'debug';
import { call, put } from 'redux-saga/effects';
import wait from '@signageos/lib/dist/Timer/wait';
import { DeviceTelemetryType } from '@signageos/common-types/dist/Device/Telemetry/DeviceTelemetryType';
import { withDependencies } from '../../../DI/dependencyInjection';
import { IPropertyStorage } from '../../../Property/propertyStorage';
import Property from '../../../Property/Property';
import { getTelemetryPropertyName } from './deviceTelemetrySagas';
import { FREQUENTLY } from '../../../Intervals/constants';

const debug = Debug('@signageos/front-display:Device:Telemetry:centralizedTelemetryInvalidation');
const warn = Debug('@signageos/front-display:Device:Telemetry:centralizedTelemetryInvalidation:warn');

const telemetryTypes = Object.values(DeviceTelemetryType).filter((value): value is DeviceTelemetryType => typeof value === 'string');

/**
 * Watches for `telemetryInvalidatedAt` configuration changes.
 * When the value changes compared to the locally stored one, removes all cached telemetry data
 * and triggers telemetry invalidation so fresh telemetry is re-sent one by one.
 */
export const centralizedTelemetryInvalidationSaga = (propertyStorage: IPropertyStorage) =>
	withDependencies(['telemetryInvalidatedAt'], function* ({ telemetryInvalidatedAt }) {
		if (!telemetryInvalidatedAt) {
			return;
		}

		const storedTelemetryInvalidatedAt = yield call(
			[propertyStorage, propertyStorage.getValueOrDefault],
			Property.TELEMETRY_INVALIDATED_AT,
			null,
		);

		debug('Telemetry invalidatedAt received', { telemetryInvalidatedAt, storedTelemetryInvalidatedAt });

		if (telemetryInvalidatedAt !== storedTelemetryInvalidatedAt) {
			debug('Telemetry invalidatedAt changed, purging cached telemetry data');

			yield call(removeCachedTelemetryData, propertyStorage);
			yield call([propertyStorage, propertyStorage.setValue], Property.TELEMETRY_INVALIDATED_AT, telemetryInvalidatedAt);

			debug('Triggering sequential telemetry invalidation');
			yield call(sequentialTelemetryInvalidation);
		}
	});

export function* sequentialTelemetryInvalidation() {
	for (const telemetryType of telemetryTypes) {
		debug(`Invalidating telemetry type: ${telemetryType}`);
		yield put({ type: Property.TELEMETRY_TYPE_INVALIDATION, name: telemetryType });
		yield call(wait, FREQUENTLY);
	}

	debug('Sequential telemetry invalidation complete');
}

export function* removeCachedTelemetryData(propertyStorage: IPropertyStorage) {
	for (const telemetryType of telemetryTypes) {
		try {
			const property = getTelemetryPropertyName(telemetryType, 'LATEST_REPORTED_SETTINGS');
			yield call([propertyStorage, propertyStorage.removeValue], property);
			debug(`Removed cached telemetry for ${telemetryType}`);
		} catch (error) {
			// Some telemetry types may not have a corresponding property, skip them
			warn(`Skipping telemetry cache removal for ${telemetryType}`, error);
		}
	}

	// Clear the telemetry stack storage as well
	yield call([propertyStorage, propertyStorage.removeValue], Property.TELEMETRY_STACK_STORAGE);
	debug('Removed telemetry stack storage');
}
